import { clamp, isEmpty, uniqBy } from 'lodash-es';
import { create } from 'zustand';

import { storageApi } from '@/api';
import { AUTH_CONST, COLLECTION_CONST } from '@/constant';
import {
  deepCopy,
  download,
  getFileByEntry,
  readEntryRecursive,
  storage,
  uuid,
  wait,
} from '@/package';
import { type S3ObjectInfo, makeKey } from '@/repository';
import type {
  CollectionManagementActions,
  CollectionManagementState,
  FileProgressStatus,
  S3ListItem,
} from '@/type';

import useTenantStorageStore from './tenantStorageStore';
import useTenantStore from './tenantStore';

const KEYS = COLLECTION_CONST.KEYS;

const ROOT_ITEM = {
  uuid: 'root',
  key: '',
  name: '/',
  isDir: true,
  children: [],
} as S3ListItem;

const INIT_STATE: CollectionManagementState = {
  connected: false,
  currentBucket:
    storage.local.get<string>(KEYS.STORAGE_COLLECTION_LATEST_BUCKET) ?? '',
  currentDirectoryUuid: ROOT_ITEM.uuid!,
  downloadFileStatusMap: {},
  itemByUuid: { root: ROOT_ITEM },
  itemList: ROOT_ITEM.children ?? [],
  selectedUuidList: [],
  storageAccessibleBucketList: storage.local.get<string[]>(
    AUTH_CONST.KEYS.STORAGE_STORAGE_ACCESSIBLE_BUCKET_LIST,
  ),
  uploadFileStatusMap: {},
};

const createFileProgressStatus = (
  param: Partial<FileProgressStatus>,
): FileProgressStatus => ({
  error: param.error,
  file: param.file ?? ({} as File),
  index: param.index ?? 0,
  isDir: param.isDir ?? false,
  key: param.key ?? '',
  percent: param.percent ?? 0,
  status: param.status ?? 'pending',
});

const genItemList = ({
  fileList,
  dirList,
}: {
  fileList: S3ObjectInfo[];
  dirList: S3ObjectInfo[];
}) => {
  const itemList: S3ListItem[] = [];

  const pushItem = (item: S3ListItem) => {
    const _uuid = item.uuid ?? uuid();

    itemList.push({
      ...item,
      uuid: _uuid,
    });
  };

  dirList.forEach(dir => {
    const key = dir.key;

    pushItem({
      name: keyToName(key),
      key,
      isDir: true,
      children: [],
      lastStartAfter: null,
    });
  });

  fileList.forEach(file => {
    const key = file.key!;

    pushItem({
      name: keyToName(key),
      key,
      isDir: false,
      eTag: file.etag ?? '',
      size: file.size ?? 0,
      updatedAt: file.lastModified ?? '',
    });
  });

  return itemList;
};

const keyToName = (key: string) => {
  const token = key.replace(/\/$/, '').split('/');

  return token[token.length - 1];
};

const replaceExceptedExt = (downloadFileName: string) => {
  const extList = downloadFileName.match(/\.[a-z0-9]+/gi) ?? [];

  if (extList.length > 1) {
    extList.pop();

    extList.forEach(v => {
      downloadFileName = downloadFileName.replace(v, v.replace('.', '_'));
    });
  }

  return downloadFileName;
};

const useCollectionManagementStore = create<
  CollectionManagementState & CollectionManagementActions
>((set, get) => {
  const getStorageState = () => useTenantStorageStore.getState();
  const checkFileSize = (file: File) =>
    file.size <= useTenantStore.getState().limitSize.fileSizeLimit * 1024 ** 2;
  const prepareFileListUpload = async (
    fileEntryListOrFileList: FileSystemFileEntry[] | FileList | File[],
  ) => {
    const { getCurrentDir } = get();
    const currentDir = getCurrentDir();

    get().clearUploadFileStatusMap();

    let fileKeyByIndex = {} as {
      [index: number]: FileProgressStatus['key'];
    };
    let uploadFileStatusMap =
      {} as CollectionManagementState['uploadFileStatusMap'];
    let index = 0;
    let hasExceedFileSize = false;

    try {
      await Promise.all(
        // oxlint-disable-next-line no-useless-spread
        Array.from([...fileEntryListOrFileList]).map(async raw => {
          const isFile = raw instanceof File || raw instanceof Blob;
          // eslint-disable-next-line prefer-const
          let [file, error] = isFile
            ? [raw, null]
            : await getFileByEntry(raw)
                .then(file => [file, null] as const)
                .catch(
                  error =>
                    [
                      new File([], raw.name),
                      new Error(error?.message ?? 'This file is not normal', {
                        cause: { type: 'FAILED_FILE_ENTRY_TO_FILE' },
                      }),
                    ] as const,
                );

          !hasExceedFileSize &&
            !checkFileSize(file) &&
            (hasExceedFileSize = true);

          const fullPath = isFile ? raw.webkitRelativePath : raw.fullPath;
          let fileKey = `${currentDir.key}${!isEmpty(fullPath) ? fullPath : file.name}`;

          fileKey = makeKey(fullPath ? `${fileKey}` : `/${fileKey}`, {
            isDir: false,
          }).replace(/(\/+)/g, '/');

          error =
            error ??
            (hasExceedFileSize
              ? new Error('Exceed file size', {
                  cause: { type: 'EXCEED_FIE_SIZE' },
                })
              : null);

          uploadFileStatusMap[fileKey] = createFileProgressStatus({
            key: fileKey,
            file,
            index,
            error,
            status: error ? 'error' : 'pending',
          });
          fileKeyByIndex[index++] = fileKey;

          return {
            file,
            fileKey,
            error,
          };
        }),
      );
    } catch (error) {
      console.warn(error);
    }

    set({
      uploadFileStatusMap: {
        ...get().uploadFileStatusMap,
        ...uploadFileStatusMap,
      },
    });

    fileKeyByIndex = {};
    uploadFileStatusMap = {};
  };
  let isUploadCancel = false;

  const upload = async () => {
    isUploadCancel = false;

    let uploadFileStatusMap = get().uploadFileStatusMap;
    const queue = [] as Promise<any>[];
    const maxQueueSize = 5;
    const uploadResultList = [] as boolean[];

    for (const key in uploadFileStatusMap) {
      let statusMap = get().uploadFileStatusMap[key];

      if (isUploadCancel) break;
      if (statusMap.error || statusMap.status === 'error') continue;
      if (queue.length >= maxQueueSize) await Promise.race(queue);

      const uploadFileOption = { key, file: statusMap.file, presignURL: '' };

      await wait(100);

      statusMap = get().uploadFileStatusMap[key];

      try {
        if (statusMap.error) throw statusMap.error;

        const [presignURL] = await get().getPresignURLList([
          { path: key, size: statusMap.file.size },
        ]);

        uploadFileOption.presignURL = presignURL ?? '';
      } catch (error) {
        console.warn(error);

        uploadFileStatusMap = {
          ...uploadFileStatusMap,
          [key]: {
            ...uploadFileStatusMap[key],
            error: error as any,
            status:
              statusMap.status !== 'canceled' ? 'error' : statusMap.status,
          },
        };

        set({ uploadFileStatusMap });

        continue;
      }

      const node = getStorageState()
        .uploadFile({
          ...uploadFileOption,
          progressCallback: progress => {
            const { error, status } = get().uploadFileStatusMap[key];

            if (status === 'canceled') {
              uploadFileStatusMap = {
                ...uploadFileStatusMap,
                [key]: {
                  ...uploadFileStatusMap[key],
                  percent: 0,
                  status,
                  error,
                },
              };
            } else {
              const percent = clamp(
                Math.round(((progress.loaded ?? 0) / progress.total) * 100),
                0,
                100,
              );

              uploadFileStatusMap = {
                ...uploadFileStatusMap,
                [key]: {
                  ...uploadFileStatusMap[key],
                  percent,
                  status: percent === 100 ? 'done' : 'progress',
                },
              };

              if (percent === 100) {
                get().readDirectory({
                  childrenOption: { autoAdd: true, replace: true },
                });
              }
            }

            set({ uploadFileStatusMap });
          },
        })
        .then(result => uploadResultList.push(result))
        .finally(() => {
          get().readDirectory();
        });

      queue.push(node);
    }

    await Promise.allSettled(queue);

    return uploadResultList;
  };

  return {
    ...INIT_STATE,

    cancelUpload() {
      isUploadCancel = true;

      const uploadFileStatusMap = get().uploadFileStatusMap;

      for (const key in uploadFileStatusMap) {
        const item = uploadFileStatusMap[key];

        if (item.status === 'pending') {
          uploadFileStatusMap[key] = {
            ...item,
            status: 'canceled',
            percent: 0,
            error: new Error('Cancel upload', {
              cause: { type: 'CANCEL_UPLOAD' },
            }),
          };
        }
      }

      set({ uploadFileStatusMap });
    },
    changeConfig: config => getStorageState().changeConfig(config),
    clearDownloadFileStatusMap() {
      set({ downloadFileStatusMap: {} });
    },
    clearUploadFileStatusMap() {
      set({ uploadFileStatusMap: {} });
    },
    async connectStorage(config) {
      try {
        getStorageState().connectStorage(config);

        set({ connected: true });
      } catch (error) {
        console.warn(error);
        set({ connected: false });
      }
    },
    createDirectory: param => getStorageState().createDirectory(param),

    deselectAll() {
      set({ selectedUuidList: [] });
    },
    downloadDirectoryList(itemList) {
      let downloadFileStatusMap = deepCopy(get().downloadFileStatusMap);

      return Promise.all(
        itemList.map((item, i) => {
          const key = item.key;
          const fileStatus = createFileProgressStatus({
            key,
            index: i,
            isDir: true,
          });
          downloadFileStatusMap[key] = fileStatus;

          set({ downloadFileStatusMap });

          return getStorageState()
            .downloadDirectory({ key })
            .then(() => {
              downloadFileStatusMap = {
                ...get().downloadFileStatusMap,
                [key]: { ...fileStatus, status: 'progress' },
              };
            })
            .catch(() => {
              downloadFileStatusMap = {
                ...get().downloadFileStatusMap,
                [key]: { ...fileStatus, status: 'error' },
              };
            })
            .finally(() => {
              set({ downloadFileStatusMap });
            });
        }),
      );
    },
    downloadFileList(itemList) {
      let downloadFileStatusMap = deepCopy(get().downloadFileStatusMap);

      return Promise.all(
        itemList.map((item, i) => {
          const key = item.key;
          const downloadFileName = item.downloadFileName;
          const fileStatus = createFileProgressStatus({
            key,
            index: i,
          });
          const isDir = downloadFileStatusMap[downloadFileName ?? key];

          downloadFileStatusMap[downloadFileName ?? key] = fileStatus;

          return getStorageState()
            .downloadFile({
              key,
              downloadCallback: progress => {
                const percent = Math.round(
                  ((progress.loaded ?? 0) / progress.total) * 100,
                );

                if (progress.total) {
                  downloadFileStatusMap = {
                    ...downloadFileStatusMap,
                    [downloadFileName ?? key]: {
                      ...downloadFileStatusMap[downloadFileName ?? key],
                      percent,
                      status: 'progress',
                    },
                  };
                }

                if (percent >= 100) {
                  downloadFileStatusMap = {
                    ...downloadFileStatusMap,
                    [downloadFileName ?? key]: {
                      ...downloadFileStatusMap[downloadFileName ?? key],
                      percent: 100,
                      status: 'done',
                    },
                  };
                }

                set({ downloadFileStatusMap });
              },
            })
            .then(({ body, fileName }) => {
              const downloadFileNameValue = fileName ?? downloadFileName;

              download(
                body,
                isDir
                  ? replaceExceptedExt(downloadFileNameValue)
                  : downloadFileNameValue,
                i * 100,
              );
            })
            .catch(error => {
              console.warn(error);

              downloadFileStatusMap = {
                ...downloadFileStatusMap,
                [downloadFileName ?? key]: {
                  ...downloadFileStatusMap[downloadFileName ?? key],
                  percent: 0,
                  status: 'error',
                  error,
                },
              };

              set({ downloadFileStatusMap });
            });
        }),
      );
    },

    getBucketList() {
      return getStorageState().storageAccessibleBucketList;
    },
    getStorageConfig: () => getStorageState().config,
    getCurrentDir() {
      const { itemByUuid } = get();

      return itemByUuid[get().currentDirectoryUuid] ?? itemByUuid.root;
    },
    getPresignURLList(fileList) {
      return storageApi
        .createPresignURL({ bucket: get().currentBucket, fileList })
        .then(res => res.data.resultData);
    },

    moveRoot() {
      const { itemByUuid } = get();

      get().setCurrentDirectoryUuid(itemByUuid.root.uuid!);
    },

    isConnected() {
      return get().connected;
    },
    readDirectory({ path, startAfter, childrenOption } = {}) {
      const { itemByUuid } = get();

      const uuid = path
        ? (Object.entries(itemByUuid).find(([_, v]) => v.key === path)?.[0] ??
          itemByUuid.root.uuid!)
        : get().currentDirectoryUuid;
      const dir = itemByUuid[uuid];
      const replace = startAfter ? true : (childrenOption?.replace ?? false);

      if (dir.isLoading) {
        return Promise.resolve([]);
      }

      if (!replace && dir.children?.length) {
        return Promise.resolve(null);
      }

      dir.isLoading = true;

      return getStorageState()
        .readDirectory({
          prefix: path ?? dir?.key ?? '',
          startAfter,
        })
        .then(res => {
          const itemByUuid = get().itemByUuid;
          const item = itemByUuid[dir.uuid!];

          item.lastStartAfter = res.lastFile?.key;

          get().setItemByUuid(itemByUuid);

          return res;
        })
        .then(genItemList)
        .then(children => children.filter(child => child.key !== dir.key))
        .then(children => {
          if (childrenOption?.autoAdd) {
            get().setChildren({
              uuid: dir.uuid!,
              children,
              appended: childrenOption.appended,
            });
          }

          return children;
        })
        .finally(() => (dir.isLoading = false));
    },
    readFile: param => getStorageState().readFile(param),
    readFileList: param => getStorageState().readFileList(param),
    async readyUploadDirectory(itemList) {
      try {
        const fileEntryList = await readEntryRecursive(itemList);

        await prepareFileListUpload(fileEntryList);
      } catch (error) {
        console.warn(error);
      }
    },
    readyUploadFileList(fileList) {
      return prepareFileListUpload(fileList);
    },
    removeDirectory: param => getStorageState().removeDirectory(param),
    removeFile: param => getStorageState().removeFile(param),
    refreshDirectory(_param) {
      // TODO: 작업 해야함
      return Promise.resolve();
    },

    select(itemList) {
      const selectedUuidList = itemList?.map(item => item.uuid!) ?? [];

      set({ selectedUuidList });
    },
    setChildren({ uuid, children, appended }) {
      const _uuid = uuid ?? get().currentDirectoryUuid;
      const itemByUuid = get().itemByUuid;
      const item = itemByUuid[_uuid];

      if (item == null) {
        console.warn(`Not found Item(${_uuid})`);

        return;
      }

      if (appended) {
        item.children = uniqBy((item.children ?? []).concat(children), 'key');
      } else {
        item.children?.forEach(child => delete itemByUuid[child.uuid!]);
        item.children = uniqBy(children, 'key');
      }

      children.forEach(child => (itemByUuid[child.uuid!] = child));

      get().setItemByUuid(itemByUuid);
    },
    setCurrentBucket(bucket: string) {
      getStorageState().setStorageCurrentBucket(bucket);
      set({ currentBucket: bucket });
      get().setItemByUuid({ root: { ...ROOT_ITEM, children: [] } });
      get().setCurrentDirectoryUuid(ROOT_ITEM.uuid!);

      storage.local.set(KEYS.STORAGE_COLLECTION_LATEST_BUCKET, bucket);
    },
    setCurrentDirectoryUuid(uuid) {
      set({ currentDirectoryUuid: uuid });

      storage.local.set(
        KEYS.STORAGE_COLLECTION_LATEST_DIR_KEY,
        get().itemByUuid[uuid].key,
      );
    },
    setDownloadFileStatus(key, progressStatus) {
      const downloadFileStatusMap = get().downloadFileStatusMap;

      set({
        downloadFileStatusMap: {
          ...downloadFileStatusMap,
          [key]: {
            ...downloadFileStatusMap[key],
            ...progressStatus,
          },
        },
      });
    },
    setItemByUuid(itemByUuid) {
      set({ itemByUuid });
      get().setItemList([...(itemByUuid.root.children ?? [])]);
    },
    setItemList(itemList) {
      set({ itemList });
    },
    setStorageAccessibleBucketList(bucketList) {
      getStorageState().setStorageAccessibleBucketList(bucketList);
      set({ storageAccessibleBucketList: bucketList });
    },

    async uploadDirectory(itemList) {
      try {
        await get().readyUploadDirectory(itemList);

        return await upload();
      } catch (error) {
        console.warn('Error reading file entries:', error);
      } finally {
        get().readDirectory();
      }

      return [];
    },

    async uploadFileList(fileList) {
      try {
        await get().readyUploadFileList(fileList);

        return await upload();
      } catch (error) {
        console.warn('Error reading file entries:', error);
      } finally {
        get().readDirectory();
      }

      return [];
    },
  };
});

export default useCollectionManagementStore;
